const initializedSections = new WeakSet();

const getSections = (scope = document) => {
  if (!scope) {
    return [];
  }

  if (scope.matches?.('.sectors-sec')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll?.('.sectors-sec') || []);
};

const getSectionItems = (section) => Array.from(section.querySelectorAll('[data-sectors-item]'))
  .map((item) => ({
    item,
    trigger: item.querySelector('[data-sectors-trigger]'),
    panel: item.querySelector('[data-sectors-panel]'),
    inner: item.querySelector('[data-sectors-panel-inner]'),
  }))
  .filter(({ trigger, panel, inner }) => trigger && panel && inner);

const updatePanelHeights = (items) => {
  items.forEach(({ panel, inner }) => {
    panel.style.setProperty('--sectors-panel-height', `${inner.scrollHeight}px`);
  });
};

const setActiveItem = (items, activeItem, isStacked = false) => {
  updatePanelHeights(items);
  const activeIndex = items.findIndex(({ item }) => item === activeItem);

  items.forEach(({ item, trigger, panel }, index) => {
    const isActive = item === activeItem;

    item.classList.toggle('is-active', isActive);
    item.classList.toggle('is-before-active', index === activeIndex - 1);
    item.classList.toggle('is-after-active', index === activeIndex + 1);
    trigger.setAttribute('aria-expanded', isStacked || isActive ? 'true' : 'false');
    panel.setAttribute('aria-hidden', isStacked || isActive ? 'false' : 'true');
    panel.inert = !(isStacked || isActive);
  });
};

const initSectorsSection = (scope = document) => {
  getSections(scope).forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const items = getSectionItems(section);

    if (!items.length) {
      return;
    }

    initializedSections.add(section);

    let currentActiveItem = items.find(({ item, trigger }) => (
      item.classList.contains('is-active') || trigger.getAttribute('aria-expanded') === 'true'
    ))?.item || items[0].item;
    const stackedMedia = window.matchMedia('(max-width: 820px)');

    let updateFrame = null;

    const updateState = () => {
      setActiveItem(items, currentActiveItem, stackedMedia.matches);
    };

    const requestHeightUpdate = () => {
      if (updateFrame) {
        return;
      }

      updateFrame = requestAnimationFrame(() => {
        updateFrame = null;
        updatePanelHeights(items);
      });
    };

    updateState();
    section.classList.add('is-ready');

    section.addEventListener('click', (event) => {
      if (stackedMedia.matches) {
        const item = event.target.closest('[data-sectors-item]');

        if (!item || !section.contains(item) || event.target.closest('a')) {
          return;
        }

        const link = item.querySelector('.sectors-sec__item-buttons .btn[href]');

        if (link?.href) {
          window.location.href = link.href;
        }

        return;
      }

      const trigger = event.target.closest('[data-sectors-trigger]');

      if (!trigger || !section.contains(trigger)) {
        return;
      }

      const nextItem = trigger.closest('[data-sectors-item]');

      if (!nextItem || nextItem.classList.contains('is-active')) {
        return;
      }

      currentActiveItem = nextItem;
      updateState();
    });

    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const trigger = event.target.closest('[data-sectors-trigger]');

      if (!trigger || !section.contains(trigger)) {
        return;
      }

      const nextItem = trigger.closest('[data-sectors-item]');

      if (!nextItem) {
        return;
      }

      event.preventDefault();
      if (stackedMedia.matches) {
        trigger.click();
        return;
      }
      currentActiveItem = nextItem;
      updateState();
    });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(requestHeightUpdate);
      items.forEach(({ inner }) => resizeObserver.observe(inner));
    }

    window.addEventListener('resize', requestHeightUpdate, { passive: true });
    stackedMedia.addEventListener?.('change', updateState);
    window.setTimeout(requestHeightUpdate, 250);
  });
};

initSectorsSection();
window.initSectorsSection = initSectorsSection;
